import { FilterOutlined, HistoryOutlined } from '@ant-design/icons'
import { Pagination, Skeleton } from 'antd'
import React from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'
import { Text } from 'sutra/components/Text/Text'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import {
  cardBorder,
  darkBlueFontColor,
  lightText,
} from 'assets/styles/variables'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import { computeGapStrength } from 'features/trend-detection/features/benchmarking-agent/gap-strength-utils'
import { PerformanceBadge } from 'features/trend-detection/features/benchmarking-agent/GapStrengthBadge'
import { SentimentGapBar } from 'features/trend-detection/features/benchmarking-agent/SentimentGapBar'
import { BENCHMARKING_FEED_PAGE_SIZE } from 'features/trend-detection/features/benchmarking-feed/hooks/use-benchmarking-feed'
import {
  EmptyHint,
  FeedSectionHeaderBlock,
  FeedSectionHeading,
  FeedSectionSubheading,
  FeedToolbarLeft,
  FeedToolbarRow,
  SortSelect,
  ToolbarSelectPrefixIcon,
  ViewPreviousFeedButton,
  ViewPreviousFeedSubtext,
  ViewPreviousFeedTitleRow,
} from 'features/trend-detection/features/issue-feed/feed-styles'
import { trendDetectionBenchmarkingAgentPath } from 'features/trend-detection/paths'
import type {
  AgentFeedOption,
  BenchmarkingFeedDirection,
  BenchmarkingFeedPeriod,
  BenchmarkingInsightFeedItem,
} from 'features/trend-detection/types'
import { formatUtcDateFromISO } from 'features/trend-detection/utils/helpers'

export interface BenchmarkingFeedViewProps {
  projectId: string | null
  items: BenchmarkingInsightFeedItem[]
  totalCount: number
  totalPages: number
  page: number
  isLoading: boolean
  hasHistoricalInsights: boolean
  agentsForFilter: AgentFeedOption[]
  agentUuidFilter: string | null
  onAgentUuidFilterChange: (uuid: string | null) => void
  directionFilter: BenchmarkingFeedDirection | null
  onDirectionFilterChange: (direction: BenchmarkingFeedDirection | null) => void
  period: BenchmarkingFeedPeriod
  onPeriodChange: (period: BenchmarkingFeedPeriod) => void
  onPageChange: (page: number) => void
  onInsightClick: (insight: BenchmarkingInsightFeedItem) => void
}

export const BenchmarkingFeedView: React.FC<BenchmarkingFeedViewProps> = ({
  projectId,
  items,
  totalCount,
  totalPages,
  page,
  isLoading,
  hasHistoricalInsights,
  agentsForFilter,
  agentUuidFilter,
  onAgentUuidFilterChange,
  directionFilter,
  onDirectionFilterChange,
  period,
  onPeriodChange,
  onPageChange,
  onInsightClick,
}) => {
  const isEmpty = !isLoading && items.length === 0
  const showBanner = period === 'current' && hasHistoricalInsights

  return (
    <>
      <FeedToolbarRow>
        <FeedToolbarLeft>
          <SortSelect
            aria-label="Period"
            triggerIcon={
              <ToolbarSelectPrefixIcon aria-hidden>
                <FilterOutlined />
              </ToolbarSelectPrefixIcon>
            }
            value={period}
            onChange={(v) => onPeriodChange(v as BenchmarkingFeedPeriod)}
            options={[
              { value: 'current', label: 'Current Period' },
              { value: 'all_time', label: 'Full History' },
            ]}
          />
          <SortSelect
            aria-label="Filter by direction"
            allowClear
            placeholder="All directions"
            triggerIcon={
              <ToolbarSelectPrefixIcon aria-hidden>
                <FilterOutlined />
              </ToolbarSelectPrefixIcon>
            }
            value={directionFilter ?? undefined}
            onChange={(v) =>
              onDirectionFilterChange(
                (v as BenchmarkingFeedDirection | undefined) ?? null,
              )
            }
            options={[
              { value: 'outperforms', label: 'Outperforms cohort' },
              { value: 'underperforms', label: 'Underperforms cohort' },
            ]}
          />
          <SortSelect
            aria-label="Filter by agent"
            allowClear
            placeholder="All agents"
            triggerIcon={
              <ToolbarSelectPrefixIcon aria-hidden>
                <FilterOutlined />
              </ToolbarSelectPrefixIcon>
            }
            value={agentUuidFilter ?? undefined}
            onChange={(v) => onAgentUuidFilterChange((v as string) ?? null)}
            options={agentsForFilter.map((a) => ({
              value: a.uuid,
              label: a.name,
            }))}
          />
        </FeedToolbarLeft>
      </FeedToolbarRow>
      <FeedSectionHeaderBlock>
        <FeedSectionHeading>Performance gaps</FeedSectionHeading>
        <FeedSectionSubheading>
          See what leads or lags in sentiment compared to the rest.
        </FeedSectionSubheading>
      </FeedSectionHeaderBlock>

      {isLoading ? (
        <SkeletonStack>
          {Array.from({ length: 2 }).map((_, i) => (
            <SkeletonCard key={i}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </SkeletonCard>
          ))}
        </SkeletonStack>
      ) : isEmpty ? (
        <EmptyHint>No results found.</EmptyHint>
      ) : (
        <InsightsList>
          {items.map((insight) => (
            <InsightCard
              key={insight.uuid}
              onClick={() => onInsightClick(insight)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onInsightClick(insight)
                }
              }}
              tabIndex={0}
              role="button"
            >
              <CardInner>
                <CardBody>
                  <PerformanceBadge
                    strength={computeGapStrength(insight.z_score)}
                    direction={
                      insight.focus_sentiment != null &&
                      insight.cohort_sentiment != null
                        ? insight.focus_sentiment >= insight.cohort_sentiment
                          ? 'leading'
                          : 'lagging'
                        : 'leading'
                    }
                  />
                  <InsightTitle>
                    {insight.title ?? 'Benchmarking Insight'}
                  </InsightTitle>
                  {insight.summary && (
                    <InsightSummary>{insight.summary}</InsightSummary>
                  )}
                  <CardFooter>
                    <FooterText>
                      {projectId && insight.agent_uuid ? (
                        <AgentLink
                          to={trendDetectionBenchmarkingAgentPath(
                            projectId,
                            insight.agent_uuid,
                          )}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {insight.agent_name}
                        </AgentLink>
                      ) : (
                        insight.agent_name
                      )}{' '}
                      ·{' '}
                      <Tooltip content="Detection date">
                        <span>{formatUtcDateFromISO(insight.create_time)}</span>
                      </Tooltip>
                    </FooterText>
                  </CardFooter>
                </CardBody>
                {insight.focus_sentiment != null &&
                  insight.cohort_sentiment != null &&
                  insight.focus_entity_name && (
                    <SentimentGapBar
                      container
                      focusSentiment={insight.focus_sentiment}
                      cohortSentiment={insight.cohort_sentiment}
                      focusEntityName={insight.focus_entity_name}
                    />
                  )}
              </CardInner>
            </InsightCard>
          ))}
        </InsightsList>
      )}

      {!isLoading && totalPages > 1 && (
        <PaginationWrapper>
          <PaginationPageSummary>
            ({Math.min(page * BENCHMARKING_FEED_PAGE_SIZE, totalCount)} of{' '}
            {totalCount} insights)
          </PaginationPageSummary>
          <Pagination
            current={page}
            total={totalCount}
            pageSize={BENCHMARKING_FEED_PAGE_SIZE}
            showSizeChanger={false}
            onChange={onPageChange}
          />
        </PaginationWrapper>
      )}

      {showBanner && (
        <ViewPreviousFeedButton
          onClick={() => onPeriodChange('all_time')}
          aria-label="View previous benchmarking insights"
        >
          <ViewPreviousFeedTitleRow>
            <HistoryOutlined />
            View earlier performance gaps
          </ViewPreviousFeedTitleRow>
          <ViewPreviousFeedSubtext>
            Explore gaps identified in past runs.
          </ViewPreviousFeedSubtext>
        </ViewPreviousFeedButton>
      )}
    </>
  )
}

const InsightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const InsightCard = styled.div`
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  transition:
    box-shadow 0.2s,
    border-color 0.2s;

  &:hover {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
    border-color: ${TREND_DETECTION_PRIMARY_BLUE};
  }

  &:focus {
    outline: 2px solid ${TREND_DETECTION_PRIMARY_BLUE};
    outline-offset: 2px;
  }
`

const CardInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const InsightTitle = styled.h3`
  font-family: Poppins, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: ${darkBlueFontColor};
  margin: 0;
`

const InsightSummary = styled.p`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0;
  color: #868d98;
  margin: 0;
`

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 16px;
`

const FooterText = styled.span`
  font-size: 13px;
  color: #666;
`

const AgentLink = styled(Link)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
  text-decoration: none;
  position: relative;
  z-index: 1;

  &:hover {
    text-decoration: underline;
  }
`

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SkeletonCard = styled.div`
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
  padding: 20px;
`

const PaginationWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 0;
`

const PaginationPageSummary = styled(Text)`
  && {
    color: ${lightText};
    font-size: 13px;
    line-height: 20px;
    white-space: nowrap;
  }
`
